import { supabase } from '@/integrations/supabase/client';

interface Draft {
  id: string;
  title: string;
  region: string;
  content: string;
  source_articles: any[];
  event_clusters: any[];
  composite_score: number;
  generation_metadata: any;
}

/**
 * Approve a draft and publish it as an interview
 */
export async function approveDraft(draftId: string, draft: Draft): Promise<void> {
  // Step 1: Create the interview from the draft
  const { data: interview, error: insertError } = await supabase
    .from('interviews')
    .insert([{
      title: draft.title,
      interviewee_name: 'Kai (AI)',
      job_title: 'AI-assistent',
      company: 'Briefr.no',
      context: `Automatisk generert artikkel for region: ${draft.region}`,
      question: 'Hva skjer i denne regionen?',
      answer: draft.content,
      tags: [draft.region, 'briefr-bullets', 'ai-generated', 'rag'],
      article_type: 'brief_bullets' as const,
      publication_status: 'published',
      is_in_feeder: false,
      timestamp: new Date().toISOString(),
      photo_url: `https://vjrredluydfrknkcclbw.supabase.co/storage/v1/object/public/photos/${draft.region}.png`,
    }])
    .select()
    .single();

  if (insertError) {
    throw new Error(`Failed to publish interview: ${insertError.message}`);
  }

  // Step 2: Update draft status to 'published' and link to the interview
  const { error: updateError } = await supabase
    .from('ai_article_drafts')
    .update({
      status: 'published',
      published_at: new Date().toISOString(),
      published_interview_id: interview.id,
      updated_at: new Date().toISOString(),
    })
    .eq('id', draftId);

  if (updateError) {
    throw new Error(`Failed to update draft status: ${updateError.message}`);
  }

  // Step 3: Update region_publish_metadata
  const { error: metadataError } = await supabase
    .from('region_publish_metadata')
    .update({
      last_published_at: new Date().toISOString(),
    })
    .eq('region', draft.region);

  if (metadataError) {
    console.error('Failed to update region metadata:', metadataError);
    // Don't throw - this is not critical
  }
}

/**
 * Reject a draft with a reason
 */
export async function rejectDraft(draftId: string, rejectionReason: string): Promise<void> {
  const { error } = await supabase
    .from('ai_article_drafts')
    .update({
      status: 'rejected',
      rejection_reason: rejectionReason,
      updated_at: new Date().toISOString(),
    })
    .eq('id', draftId);

  if (error) {
    throw new Error(`Failed to reject draft: ${error.message}`);
  }
}
